import { GoogleGenerativeAI } from '@google/generative-ai'
import { QuarterlyMetrics } from '../types/quarterly-report'

const CACHE_PREFIX = 'ai_survey_quarterly_'
const CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 hours

interface CacheEntry {
    data: QuarterlyInsightResponse
    timestamp: number
}

export interface QuarterlyInsightResponse {
    summary: string
    customer_health: {
        churn_analysis: string
        acquisition_trend: string
        health_overview: string
    }
    satisfaction_trends: {
        overall_trend: string
        service_areas: { name: string; analysis: string }[]
    }
    upsale_opportunities: string[]
    key_insights: string[]
    recommendations: string[]
    comparison_insights?: string
}

export interface QuarterlyInsightRequest {
    data: QuarterlyMetrics[]
    period_range: string
    comparison_data?: QuarterlyMetrics[]
    comparison_period_range?: string
}

// Stable hash from quarterly data
const createDataHash = (data: QuarterlyMetrics[]): string => {
    if (!data || data.length === 0) return 'empty'

    const hashString = JSON.stringify({
        length: data.length,
        firstQuarter: data[0]?.quarter,
        lastQuarter: data[data.length - 1]?.quarter,
        totalParticipants: data.reduce(
            (sum, q) => sum + q.survey_participants,
            0
        ),
    })

    let hash = 0
    for (let i = 0; i < hashString.length; i++) {
        const char = hashString.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash
    }

    return Math.abs(hash).toString(36)
}

const getCachedAnalysis = (
    cacheKey: string
): QuarterlyInsightResponse | null => {
    try {
        const cached = localStorage.getItem(cacheKey)
        if (!cached) return null

        const entry: CacheEntry = JSON.parse(cached)
        if (Date.now() - entry.timestamp > CACHE_DURATION) {
            localStorage.removeItem(cacheKey)
            return null
        }

        return entry.data
    } catch {
        return null
    }
}

const setCachedAnalysis = (
    cacheKey: string,
    data: QuarterlyInsightResponse
): void => {
    try {
        localStorage.setItem(
            cacheKey,
            JSON.stringify({ data, timestamp: Date.now() } as CacheEntry)
        )
    } catch (error) {
        console.error('Error caching quarterly analysis:', error)
    }
}

const initializeGemini = () => {
    const apiKey = import.meta.env.APP_GEMINI_API_KEY
    if (!apiKey) throw new Error('Gemini API key not found')

    const genAI = new GoogleGenerativeAI(apiKey)
    return genAI.getGenerativeModel({
        model: 'gemini-2.0-flash-lite',
        generationConfig: { responseMimeType: 'application/json' },
    })
}

const formatPercent = (n: number | null) =>
    n == null ? 'Không có dữ liệu' : `${n.toFixed(1)}%`
const formatScore = (n: number | null) =>
    n == null ? 'Không có dữ liệu' : `${n.toFixed(2)}/10`

const buildQuarterSummary = (q: QuarterlyMetrics) => ({
    quy: `${q.quarter}/${q.year}`,
    khach_dau_ky: q.active_customers_start,
    khach_moi: q.new_customers,
    khach_roi: q.churned_customers,
    khach_cuoi_ky: q.active_customers_end,
    ty_le_roi: formatPercent(
        q.active_customers_start > 0
            ? (q.churned_customers / q.active_customers_start) * 100
            : 0
    ),
    so_luot_khao_sat: q.survey_participants,
    khach_du_5_tieu_chi: q.rated_participants,
    pham_vi_khach_hang: 'Toàn công ty; các bộ lọc chỉ áp dụng cho khảo sát',
    ty_le_hai_long_cao: formatPercent(q.high_satisfaction_rate),
    diem_chat_luong_dich_vu: formatScore(q.avg_service_rating),
    diem_cham_soc_khach_hang: formatScore(q.avg_customer_care_rating),
    diem_ho_tro_ky_thuat: formatScore(q.avg_tech_support_rating),
    diem_xu_ly_su_co: formatScore(q.avg_issue_resolution_rating),
    diem_san_sang_gioi_thieu: formatScore(q.avg_referral_willingness_rating),
    ban_them_dich_vu: q.upsale_potential,
})

export const analyzeQuarterlyInsights = async (
    request: QuarterlyInsightRequest,
    forceRefresh = false
): Promise<QuarterlyInsightResponse> => {
    const dataHash = createDataHash(request.data)
    const comparisonHash = request.comparison_data
        ? createDataHash(request.comparison_data)
        : 'none'
    const cacheKey = `${CACHE_PREFIX}${request.period_range}_${dataHash}_${comparisonHash}`

    if (!forceRefresh) {
        const cached = getCachedAnalysis(cacheKey)
        if (cached) return cached
    }

    const dataSummary = request.data.map(buildQuarterSummary)

    let comparisonSection = ''
    if (request.comparison_data && request.comparison_period_range) {
        const compSummary = request.comparison_data.map(buildQuarterSummary)
        const satisfactionRate = (quarters: QuarterlyMetrics[]) => {
            const rated = quarters.filter(
                (q) =>
                    q.high_satisfaction_rate != null && q.rated_participants > 0
            )
            const count = rated.reduce(
                (sum, q) => sum + q.rated_participants,
                0
            )
            return count
                ? rated.reduce(
                      (sum, q) =>
                          sum +
                          q.high_satisfaction_rate! * q.rated_participants,
                      0
                  ) / count
                : null
        }
        const currAvgSat = satisfactionRate(request.data)
        const prevAvgSat = satisfactionRate(request.comparison_data)
        const currChurn = request.data.reduce(
            (s, q) => s + q.churned_customers,
            0
        )
        const prevChurn = request.comparison_data.reduce(
            (s, q) => s + q.churned_customers,
            0
        )

        comparisonSection = `
        DỮ LIỆU KỲ SO SÁNH (${request.comparison_period_range}):
        ${JSON.stringify(compSummary, null, 2)}

        THAY ĐỔI SO VỚI KỲ TRƯỚC:
        - Tỷ lệ hài lòng cao: ${formatPercent(prevAvgSat)} → ${formatPercent(currAvgSat)} (${currAvgSat != null && prevAvgSat != null ? (currAvgSat - prevAvgSat).toFixed(1) + '%' : 'Không đủ dữ liệu so sánh'})
        - Tổng khách rời: ${prevChurn} → ${currChurn}
        `
    }

    const prompt = `
    Bạn là chuyên gia Customer Success với nhiều năm kinh nghiệm trong lĩnh vực dịch vụ công nghệ.

    NHIỆM VỤ:
    Phân tích dữ liệu khảo sát hài lòng khách hàng theo quý và đưa ra insights về sức khỏe khách hàng, xu hướng chất lượng dịch vụ, và cơ hội upsale.

    DỮ LIỆU KHẢO SÁT (${request.period_range}):
    ${JSON.stringify(dataSummary, null, 2)}

    ${comparisonSection}

    YÊU CẦU PHÂN TÍCH:

    1. **Tóm tắt**: 2-3 câu tổng quan tình hình khách hàng trong kỳ

    2. **Sức khỏe khách hàng**:
       - churn_analysis: Phân tích tỷ lệ khách rời - xu hướng, quý nào cao/thấp, nguyên nhân có thể
       - acquisition_trend: Xu hướng khách mới - quý nào tốt, cơ hội mở rộng
       - health_overview: Đánh giá tổng quan tệp khách hàng

    3. **Xu hướng hài lòng**:
       - overall_trend: Xu hướng hài lòng tổng thể qua các quý
       - service_areas: Phân tích từng tiêu chí (chất lượng dịch vụ, chăm sóc khách hàng, hỗ trợ kỹ thuật, xử lý sự cố, sẵn sàng giới thiệu) - tiêu chí nào mạnh/yếu

    4. **Cơ hội bán thêm**: 3-4 cơ hội bán thêm dịch vụ dựa trên dữ liệu (Cloud, CDN, VNIS)

    5. **Nhận định chính**: 3-5 nhận định quan trọng nhất từ dữ liệu

    6. **Đề xuất**: 3-5 hành động cụ thể để cải thiện chất lượng dịch vụ và giữ chân khách hàng

    ${request.comparison_data ? '7. **So sánh kỳ trước**: Phân tích thay đổi giữa 2 kỳ, nêu những điểm thay đổi đáng chú ý' : ''}

    Trả về JSON với format:
    {
        "summary": "...",
        "customer_health": {
            "churn_analysis": "...",
            "acquisition_trend": "...",
            "health_overview": "..."
        },
        "satisfaction_trends": {
            "overall_trend": "...",
            "service_areas": [
                { "name": "Tên tiêu chí bằng tiếng Việt", "analysis": "..." }
            ]
        },
        "upsale_opportunities": ["...", "..."],
        "key_insights": ["...", "..."],
        "recommendations": ["...", "..."]${request.comparison_data ? ',\n        "comparison_insights": "..."' : ''}
    }

    NGUYÊN TẮC BẮT BUỘC:
    - Toàn bộ nội dung phải viết bằng tiếng Việt, KHÔNG dùng từ tiếng Anh
    - Thay thế: "churn" → "khách rời/tỷ lệ rời", "upsale/upsell" → "bán thêm dịch vụ", "insight" → "nhận định", "trend" → "xu hướng", "customer success" → "chăm sóc khách hàng", "highlight" → "nổi bật"
    - Tên tiêu chí trong service_areas phải là tiếng Việt (vd: "Chất lượng dịch vụ", "Chăm sóc khách hàng", ...)
    - Đưa ra số liệu cụ thể khi phân tích
    `

    try {
        const model = initializeGemini()
        const result = await model.generateContent(prompt)
        const report: QuarterlyInsightResponse = JSON.parse(
            result.response.text()
        )
        setCachedAnalysis(cacheKey, report)
        return report
    } catch (error) {
        console.error('Gemini quarterly analysis failed:', error)
        throw error
    }
}
